import React, { useState, useMemo } from 'react';
import { Button, Modal, Form, Card, Collapse } from 'react-bootstrap';
import { CreditCard, CheckCircle, Trash2, Copy, Info, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';

const PaymentStep = ({
    totalVat,
    paymentMadeDate,
    handlePaymentMade,
    handleClearPayment,
    vatPeriod,
    operationInProgress,
    companyOrgNr = '556000-0175' // TODO: Get from company settings
}) => {
    const [showDateModal, setShowDateModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [showInstructions, setShowInstructions] = useState(false);

    // Generate OCR number for Skatteverket VAT payment
    // Format: 16[OrgNr without dashes]02
    // Example: For org.nr 556000-0175 → 16556000017502
    const ocrNumber = useMemo(() => {
        if (!companyOrgNr) return '';
        const orgNr = companyOrgNr.replace(/\D/g, ''); // Remove all non-digits
        return `16${orgNr}02`;
    }, [companyOrgNr]);

    const copyOCR = () => {
        navigator.clipboard.writeText(ocrNumber);
        toast.success('OCR-nummer kopierat');
    };

    const handleSubmitPaymentDate = () => {
        handlePaymentMade(selectedDate);
        setShowDateModal(false);
    };

    return (
        <div className="step-container">
            <style>{`
                .payment-info-card {
                    background: white;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }

                .amount-display {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                }

                .ocr-display {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    font-family: 'Courier New', monospace;
                    font-size: 1.125rem;
                    font-weight: 600;
                    letter-spacing: 0.05em;
                    color: #1e293b;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .copy-button {
                    background: #3b82f6;
                    border: none;
                    color: white;
                    padding: 0.5rem 0.75rem;
                    border-radius: 6px;
                    transition: all 0.2s;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .copy-button:hover {
                    background: #2563eb;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
                }

                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    margin-bottom: 0.75rem;
                }

                .toggle-instructions-btn {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-bottom: 1.5rem;
                }

                .toggle-instructions-btn:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }

                .instructions-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 1.25rem;
                    margin-bottom: 1.5rem;
                }

                .instruction-step {
                    display: flex;
                    align-items: start;
                    margin-bottom: 1rem;
                    padding: 0.75rem;
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .instruction-step:last-child {
                    margin-bottom: 0;
                }

                .step-number {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    background: #3b82f6;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 0.875rem;
                    margin-right: 1rem;
                }

                .payment-complete-card {
                    background: white;
                    border: 2px solid #10b981;
                    border-radius: 12px;
                    padding: 1.5rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                }
            `}</style>

            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#f59e0b',
                            color: 'white'
                        }}
                    >
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Betala Momsen</h3>
                        <p className="text-muted mb-0">Betala momsen till Skatteverket</p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                {paymentMadeDate ? (
                    <div className="payment-complete-card">
                        <div className="d-flex align-items-start justify-content-between">
                            <div className="d-flex align-items-start">
                                <CheckCircle size={24} className="text-success me-3 mt-1" style={{ flexShrink: 0 }} />
                                <div>
                                    <h5 className="mb-2 fw-semibold text-success">Betalning registrerad</h5>
                                    <div className="d-flex align-items-center mb-2 text-muted">
                                        <Calendar size={16} className="me-2" />
                                        <span>{moment(paymentMadeDate).format('D MMMM YYYY')}</span>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                        Betalningen av {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalVat)} har registrerats.
                                    </div>
                                </div>
                            </div>
                            {vatPeriod?.state === 'open' && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleClearPayment}
                                    disabled={operationInProgress}
                                    className="d-inline-flex align-items-center"
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    <Trash2 size={14} className="me-1" />
                                    Ångra
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="payment-info-card">
                            <div className="d-flex align-items-center mb-3">
                                <CreditCard size={20} className="text-primary me-2" />
                                <h5 className="mb-0 fw-semibold">Betalningsinformation</h5>
                            </div>

                            <div className="amount-display mb-3">
                                <div className="text-muted small mb-2">Belopp att betala</div>
                                <div className="text-dark" style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalVat)}
                                </div>
                            </div>

                            <div className="info-row mb-2">
                                <div className="text-muted small">Mottagare</div>
                                <div className="fw-medium">Skatteverket</div>
                            </div>

                            <div className="info-row mb-2">
                                <div className="text-muted small">Bankgiro</div>
                                <div className="fw-medium">5050-1055</div>
                            </div>

                            <div>
                                <div className="text-muted small mb-2">OCR-nummer</div>
                                <div className="ocr-display">
                                    <span>{ocrNumber}</span>
                                    <button
                                        className="copy-button"
                                        onClick={copyOCR}
                                    >
                                        <Copy size={14} />
                                        <span className="small">Kopiera</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button
                            className="toggle-instructions-btn"
                            onClick={() => setShowInstructions(!showInstructions)}
                        >
                            <div className="d-flex align-items-center">
                                <Info size={18} className="text-primary me-2" />
                                <span className="fw-medium">Så här betalar du</span>
                            </div>
                            {showInstructions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        <Collapse in={showInstructions}>
                            <div className="instructions-card">
                                <div className="instruction-step">
                                    <div className="step-number">1</div>
                                    <div>
                                        <div className="fw-medium mb-1">Skapa betalning i din bank</div>
                                        <div className="text-muted small">
                                            Logga in i din internetbank och skapa en ny betalning
                                        </div>
                                    </div>
                                </div>

                                <div className="instruction-step">
                                    <div className="step-number">2</div>
                                    <div>
                                        <div className="fw-medium mb-1">Fyll i mottagare och belopp</div>
                                        <div className="text-muted small">
                                            Mottagare: <strong>Skatteverket</strong><br/>
                                            Bankgiro: <strong>5050-1055</strong><br/>
                                            Belopp: <strong>{new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalVat)}</strong><br/>
                                            OCR: <strong>{ocrNumber}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="instruction-step">
                                    <div className="step-number">3</div>
                                    <div>
                                        <div className="fw-medium mb-1">Skicka betalningen</div>
                                        <div className="text-muted small">
                                            Bekräfta och skicka betalningen. Spara kvittot.
                                        </div>
                                    </div>
                                </div>

                                <div className="instruction-step">
                                    <div className="step-number">4</div>
                                    <div>
                                        <div className="fw-medium mb-1">Markera som betalt här</div>
                                        <div className="text-muted small">
                                            När betalningen är skickad, klicka på knappen nedan
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Collapse>

                        <Button
                            variant="success"
                            onClick={() => setShowDateModal(true)}
                            disabled={operationInProgress}
                            className="d-inline-flex align-items-center"
                            size="lg"
                        >
                            <CheckCircle size={18} className="me-2" />
                            Markera betalning som klar
                        </Button>
                    </>
                )}
            </div>

            {/* Payment Date Modal */}
            <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>
                        <div className="d-flex align-items-center">
                            <Calendar size={22} className="text-success me-2" />
                            <span>Bekräfta betalning</span>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <div className="mb-3 p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div className="d-flex align-items-center mb-2">
                            <Info size={16} className="text-primary me-2" />
                            <small className="fw-medium">Betalningsinformation</small>
                        </div>
                        <div className="small text-muted">
                            <div>Belopp: <strong>{new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(totalVat)}</strong></div>
                            <div>OCR: <strong>{ocrNumber}</strong></div>
                        </div>
                    </div>

                    <Form.Group>
                        <Form.Label className="fw-medium">När betalades momsen?</Form.Label>
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="form-control-lg"
                        />
                        <Form.Text className="text-muted">
                            Välj det datum när betalningen skickades till Skatteverket
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDateModal(false)}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleSubmitPaymentDate}
                        className="d-inline-flex align-items-center"
                    >
                        <CheckCircle size={16} className="me-2" />
                        Bekräfta betalning
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PaymentStep;